import './PricingSection.scss'

import { memo, useEffect, useState, useCallback, useMemo } from 'react'

import {
  Box,
  BulletList,
  BulletListContent,
  Button,
  Column,
  Divider,
  FlexBox,
  GridContainer,
  H2,
  Icon,
  ListItem,
  P,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Row,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
  Visible,
} from '@vp/swan'

import { PackageCardModel } from '@vp/digital-common-data-model-lib-ubik'
import { isProduction } from '@vp/digital-environment-lib-ubik'
import { PageAnalyticsData } from '@vp/digital-tracking-lib-ubik'

import { trackPlansSectionFindOutMoreClick, trackPlansSectionViewMorePlanDetailsClick } from '../WixWebsitesAnalytics'
import MissingContentAlert from '../MissingContentAlert'
import { isEnabledPlan, FREE_PACKAGE } from '../../helpers/packageTierMapper'
import { SubscriptionOptionsEnum } from '../helpers/WixPageAnalyticsProvider'
import WixNewPackageGridHeader from '../wixNewPackageGrid/WixNewPackageGridHeader'
import PaymentsControlContainer from '../newPaymentsControlContainer/PaymentsControlContainer'
import { useSignedInAction } from '../helpers/useSignedInAction'

import { useIdentity, useLogger } from '@vp/ubik-context'
import { getPartnerAccountSiteIds } from '../helpers/partnerAccountHelpers'
import { Hero } from '../../../types/hero'
import { CommonFeatures } from '../../../types/commonFeatures'
import useSessionPlan from '../hooks/useSessionPlan/useSessionPlan'
import { DomainsModalSearch } from '../../../common/modals/CheckAvailableDomainsModal/CheckAvailableDomainsModal'

interface PricingSectionProps {
  content?: { heros: Hero[] }
  packageGridPlans: any
  locale: string
  productVersion: any
  loadingPlans?: string[]
  handleGetStartedNavigation: Function
  handleWixPlansPage: Function
  handleWixUpgradePage: Function
  trackToggleClickedEvent: Function
  pageAnalyticsData: PageAnalyticsData
  accessibility: CommonFeatures
  domainsModalSearch?: DomainsModalSearch
  isPaymentMethodActive?: boolean
}

interface ViewMorePlanDetailsProps {
  label: string
  onClick: () => void
}

const ViewMorePlanDetails = memo(({ label, onClick }: ViewMorePlanDetailsProps) => (
  <Button skin="secondary" onClick={onClick}>
    {label}
  </Button>
))

const PricingSection = ({
  content,
  packageGridPlans,
  locale,
  productVersion,
  loadingPlans,
  handleGetStartedNavigation,
  handleWixPlansPage,
  handleWixUpgradePage,
  trackToggleClickedEvent,
  pageAnalyticsData,
  accessibility,
  domainsModalSearch,
  isPaymentMethodActive = true,
}: PricingSectionProps) => {
  const [isBilledYearly, setIsBilledYearly] = useState(true)
  const [isShorterJourneyEnabled, setIsShorterJourneyEnabled] = useState(false)
  const [isLoadingPartnerAccount, setIsLoadingPartnerAccount] = useState(true)
  const [siteId, setSiteId] = useState('')
  const { auth, isSignedIn } = useIdentity()
  const canonicalId = auth?.getCanonicalId()
  const { setSelectedPlan } = useSessionPlan()
  const packages = useMemo(() => {
    const basePackages = packageGridPlans?.packages ?? []
    // Step 1: Filter Validity
    // Logic A: Normal Order
    let validPackages = basePackages.filter((plan: PackageCardModel) => isEnabledPlan(plan.key ?? ''))
    validPackages = [...validPackages].reverse()

    // Step 2: Filter specifically for this journey (Remove free plan)
    const displayablePackages = isShorterJourneyEnabled ? validPackages.filter((plan: any) => plan.key !== FREE_PACKAGE) : validPackages
    return displayablePackages
  }, [packageGridPlans, isShorterJourneyEnabled])

  const logger = useLogger()

  const fetchPartnerAccount = useCallback(async (locale: string) => {
    try {
      const data = await getPartnerAccountSiteIds(locale, logger, 'wix-product-details-page')
      const websitesCount = data?.length ?? 0
      if (websitesCount === 0) {
        logger.debug('Could not run experiment due to missing partner account data', { data })
        setIsShorterJourneyEnabled(false)
        return
      }
      if (websitesCount === 1) {
        setSiteId(data[0])
      }
      setIsShorterJourneyEnabled(true)
    } catch (error) {
      logger.warn('Failed at fetching partner account group :', error)
      setIsShorterJourneyEnabled(false)
    } finally {
      setIsLoadingPartnerAccount(false)
    }
  }, [])

  const [, , initiateFetchPartnerAccount] = useSignedInAction<string, void>(fetchPartnerAccount, 'fetch-partner-account-after-sign-in')

  useEffect(() => {
    if (!canonicalId) return
    if (!isSignedIn) {
      setIsShorterJourneyEnabled(false)
      setIsLoadingPartnerAccount(false)
      return
    }
    initiateFetchPartnerAccount(locale)
  }, [canonicalId, isSignedIn, locale, initiateFetchPartnerAccount])

  const refactoredPackageGridPlans = useMemo(
    () => ({
      ...packageGridPlans,
      packages: packages.map((plan: any) => ({
        ...plan,
        isLoading: isLoadingPartnerAccount && plan.key !== FREE_PACKAGE,
        selectPackageButtons: plan.selectPackageButtons.map((button: any, index: any) => ({
          ...button,
          isLoading: isLoadingPartnerAccount && plan.key !== FREE_PACKAGE && index === 0,
        })),
      })),
    }),
    [packageGridPlans, packages, isLoadingPartnerAccount],
  )

  const plansCtasIndex = useMemo(
    () =>
      packages.map((plan: PackageCardModel) => {
        if (plan.key === FREE_PACKAGE) return 2
        return isShorterJourneyEnabled ? 0 : 3
      }),
    [packages, isShorterJourneyEnabled],
  )

  const handleViewMorePlanDetails = useCallback(() => {
    setTimeout(() => trackPlansSectionViewMorePlanDetailsClick(), 0)
    handleWixPlansPage()
  }, [handleWixPlansPage])

  const toggleBillingCycle = useCallback(() => {
    const wasYearly = isBilledYearly
    setIsBilledYearly((prev) => !prev)
    setTimeout(() => trackToggleClickedEvent(pageAnalyticsData, wasYearly ? SubscriptionOptionsEnum.MONTHLY : SubscriptionOptionsEnum.ANNUALLY), 0)
  }, [isBilledYearly, trackToggleClickedEvent, pageAnalyticsData])

  const onPackageSelected = useCallback(
    (plan: PackageCardModel) => {
      if (plan.key === FREE_PACKAGE) {
        handleGetStartedNavigation()
      } else {
        trackPlansSectionFindOutMoreClick(plan.key!)

        const index = plan.key === FREE_PACKAGE ? 2 : isShorterJourneyEnabled ? 0 : 3

        if (index === 0) {
          setSelectedPlan(plan)
          handleWixUpgradePage(siteId, plan.key)
        } else if (index === 3) {
          handleWixPlansPage()
        }
      }
    },
    [handleGetStartedNavigation, handleWixUpgradePage, handleWixPlansPage, isShorterJourneyEnabled, siteId, setSelectedPlan],
  )

  if (!content || !packageGridPlans || content?.heros?.length < 3) {
    return <MissingContentAlert />
  }

  // get override params for testing
  let isOverrideSales: boolean = false
  if (!isProduction() && typeof window !== 'undefined' && window.location?.search) {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    isOverrideSales = urlParams?.get('sales') === 'true' && isBilledYearly
  }

  return (
    <GridContainer>
      <Row>
        <Column span={12} textAlign={'left'}>
          <H2 m={0} fontSize="x2large">
            {content?.heros?.[0].title}
          </H2>
        </Column>

        <Column span={7}>
          <P fontSize="standard">{content?.heros?.[0].subTitle}</P>
        </Column>

        <Column span={5} mt={5}>
          <BillingCycleToggle content={content} isBilledYearly={isBilledYearly} toggleBillingCycle={toggleBillingCycle} accessibility={accessibility} />
        </Column>
      </Row>

      {/* plans header */}
      <Row mt={5}>
        <Column span={12}>
          <WixNewPackageGridHeader
            plansCtasIndex={plansCtasIndex}
            packageGridPlans={refactoredPackageGridPlans}
            productVersion={productVersion}
            highlightedPlans={['Business']}
            isBilledYearly={isBilledYearly}
            selectPackageCallback={onPackageSelected}
            overrideSales={isOverrideSales}
            showDomainAvailability={false}
            defaultSkin={isShorterJourneyEnabled ? 'primary' : 'secondary'}
            accessibility={{
              translations: accessibility,
            }}
            loadingPlans={loadingPlans}
            domainsModalSearch={domainsModalSearch}
          />
        </Column>
      </Row>

      {isPaymentMethodActive && content?.heros?.[2].heroFeatures && (
        <Row className={'pricing-section__payment-container'}>
          <Column span={12} mt={{ xs: 6, sm: 0 }}>
            <PaymentsControlContainer content={content.heros[2].heroFeatures} isPlanPage={false} />
          </Column>
        </Row>
      )}

      {content?.heros?.[2].actions?.[1].name && (
        <Row>
          <Column span={12} textAlign="center" mt={isPaymentMethodActive ? 7 : { xs: 6, sm: 0 }}>
            <ViewMorePlanDetails label={content.heros[2].actions[1].name} onClick={handleViewMorePlanDetails} />
          </Column>
        </Row>
      )}
    </GridContainer>
  )
}

interface BillingCycleToggleProps {
  content?: { heros: Hero[] }
  isBilledYearly: boolean
  toggleBillingCycle: Function
  accessibility: CommonFeatures
}

const BillingCycleToggle = ({ content, isBilledYearly, toggleBillingCycle, accessibility }: BillingCycleToggleProps) => (
  <>
    <FlexBox gap="5" alignItems="center" flexDirection="row" className="wix-new-plans-page__billing-toggle__container">
      <ToggleSwitch
        mr={0}
        activated={isBilledYearly}
        onRequestActivatedChange={() => toggleBillingCycle()}
        aria-label={content?.heros[1].title}
        aria-details={accessibility?.features?.[11].name}
      >
        <ToggleSwitchOnLabel aria-hidden={isBilledYearly ? 'false' : 'true'} aria-describedby="yearly-savings">
          {content?.heros[1].title}
        </ToggleSwitchOnLabel>

        <ToggleSwitchOffLabel textColor="subtle" aria-hidden={isBilledYearly ? 'true' : 'false'} aria-describedby="yearly-savings">
          {content?.heros[1].title}
        </ToggleSwitchOffLabel>
      </ToggleSwitch>

      <Visible xs style={{ flexGrow: 1 }}>
        <Divider />
      </Visible>
    </FlexBox>

    <Box
      mt={3}
      textColor="promo"
      fontSkin="footnote-bold"
      id="yearly-savings"
      aria-details={content?.heros[1].description}
      className="wix-new-plans-page__billing-disclaimer__container"
    >
      {isBilledYearly && content?.heros[1].description}

      {!isBilledYearly && (
        <Popover className="wix-new-plans-page__billing-toggle__popover">
          <PopoverLauncher>
            <Typography textColor="standard" fontSize="xsmall" component="span" fontWeight="bold">
              {content?.heros?.[1].description}{' '}
              <Typography tabIndex={0} component="span" textColor="error" className="wix-new-plans-page__billing-toggle__description__error">
                {content?.heros?.[1].footerDescription}
              </Typography>
            </Typography>
          </PopoverLauncher>

          <PopoverContent className="wix-new-plans-page__billing-toggle__popover__content">
            <P fontSize="small" m={0}>
              {content?.heros?.[1].heroFeatures?.title}
            </P>

            <P fontSize="small" m={0} fontWeight="bold">
              {content?.heros?.[1].heroFeatures?.description}
            </P>

            <BulletList mt="5">
              {content?.heros?.[1].heroFeatures?.features?.map((feature: any) => (
                <ListItem key={feature.key} p={0} mb="2">
                  <FlexBox alignItems="center">
                    <Icon iconType="toggleOff" size="16p" mr="3" />

                    <BulletListContent component="p" className="text-size-6" fontSize="small" m={0}>
                      {feature.name}
                    </BulletListContent>
                  </FlexBox>
                </ListItem>
              ))}
            </BulletList>
          </PopoverContent>
        </Popover>
      )}
    </Box>
  </>
)

export default memo(PricingSection)
